// Input sanitization utilities for enhanced security

// HTML entity encoding to prevent XSS
const htmlEntityMap: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
  '/': '&#x2F;',
};

/**
 * Sanitizes HTML content by encoding special characters
 */
export const sanitizeHtml = (input: string): string => {
  return input.replace(/[&<>"'\/]/g, (s) => htmlEntityMap[s]);
};

/**
 * Sanitizes user input for chat messages
 */
export const sanitizeChatInput = (input: string): string => {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // Remove null bytes and control characters (except newlines and tabs)
  let sanitized = input.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  
  // Limit length to prevent DoS
  const MAX_LENGTH = 10000;
  if (sanitized.length > MAX_LENGTH) {
    sanitized = sanitized.substring(0, MAX_LENGTH);
  }
  
  // Remove excessive whitespace
  sanitized = sanitized.replace(/\s{3,}/g, '  ').trim();
  
  return sanitized;
};

/**
 * Validates file uploads for security
 */
export const validateFileUpload = (file: File): { valid: boolean; error?: string } => {
  // Check file size (max 10MB)
  const MAX_SIZE = 10 * 1024 * 1024;
  if (file.size > MAX_SIZE) {
    return { valid: false, error: 'File size exceeds 10MB limit' };
  }

  // Check file type
  const allowedTypes = [
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'image/gif',
    'image/webp'
  ];
  
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'Invalid file type. Only images are allowed.' };
  }

  // Check filename for malicious patterns
  const filename = file.name.toLowerCase();
  const dangerousPatterns = [
    /\.php$/,
    /\.jsp$/,
    /\.asp$/,
    /\.js$/,
    /\.html$/,
    /\.htm$/,
    /\.exe$/,
    /\.bat$/,
    /\.cmd$/,
    /\.scr$/,
    /\.vbs$/,
    /\.ps1$/
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(filename)) {
      return { valid: false, error: 'File type not allowed for security reasons' };
    }
  }

  return { valid: true };
};

/**
 * Sanitizes URLs to prevent XSS and other attacks
 */
export const sanitizeUrl = (url: string): string => {
  if (!url || typeof url !== 'string') {
    return '';
  }

  // Remove dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:', 'ftp:'];
  const lowerUrl = url.toLowerCase().trim();
  
  for (const protocol of dangerousProtocols) {
    if (lowerUrl.startsWith(protocol)) {
      return '';
    }
  }

  // Only allow http and https
  if (!lowerUrl.startsWith('http://') && !lowerUrl.startsWith('https://')) {
    return '';
  }

  return url.trim();
};

/**
 * Rate limiting helper for client-side prevention
 */
export class ClientRateLimit {
  private attempts: Map<string, number[]> = new Map();
  private readonly maxAttempts: number;
  private readonly windowMs: number;

  constructor(maxAttempts: number = 10, windowMs: number = 60000) {
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
  }

  canAttempt(key: string): boolean {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    
    // Remove old attempts outside the window
    const validAttempts = attempts.filter(time => now - time < this.windowMs);
    
    if (validAttempts.length >= this.maxAttempts) {
      return false;
    }

    // Record this attempt
    validAttempts.push(now);
    this.attempts.set(key, validAttempts);
    
    return true;
  }

  reset(key: string): void {
    this.attempts.delete(key);
  }
}